import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { fetchViewerTree as fetchTree, fetchViewerObject as fetchObject } from '../../actions/infoTerminalViewer';
import { flattenTree, buildDescendantSet } from './utils';
import { useViewportMeta } from '../../hooks/useViewportMeta';
import ObjectTree from './ObjectTree';
import SchemaCanvas from './SchemaCanvas';
import InfoPanel from './InfoPanel';
import FloorSelector from './FloorSelector';
import styles from './styles.module.scss';

export default function InfoTerminalViewer() {
  const [allObjects, setAllObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // schemaObject — объект, чья схема показывается на canvas
  const [schemaObject, setSchemaObject] = useState(null);
  const [schemaDetails, setSchemaDetails] = useState(null);
  // selectedNode — выбранный узел (подсветка в дереве + InfoPanel)
  const [selectedNode, setSelectedNode] = useState(null);
  // panelOpen — показывать ли правую панель (не сбрасывает selectedNode)
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTerritoryId, setActiveTerritoryId] = useState(null);
  const [activeFloor, setActiveFloor] = useState(null);

  const [burgerOpen, setBurgerOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);

  useViewportMeta();

  const territories = useMemo(
    () => allObjects.filter(o => o.parent_id === undefined || o.parent_id === null),
    [allObjects]
  );

  const activeTerritory = useMemo(
    () => territories.find(t => t.id === activeTerritoryId) || territories[0] || null,
    [territories, activeTerritoryId]
  );

  const visibleObjects = useMemo(() => {
    if (!activeTerritory) return allObjects;
    const set = buildDescendantSet(activeTerritory.id, allObjects);
    return allObjects.filter(o => set.has(o.id));
  }, [activeTerritory, allObjects]);

  useEffect(() => {
    fetchTree()
      .then(data => { setAllObjects(flattenTree(data)); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    if (territories.length && !activeTerritoryId) {
      const first = territories[0];
      setActiveTerritoryId(first.id);
      setSelectedNode(first);
      setSchemaObject(first.has_image ? first : null);
    }
  }, [territories.length, activeTerritoryId]);

  // Загружаем детали схемы при смене schemaObject
  useEffect(() => {
    if (!schemaObject) { setSchemaDetails(null); return; }
    setSchemaDetails(null);
    fetchObject(schemaObject.id)
      .then(setSchemaDetails)
      .catch(() => setSchemaDetails(null));
  }, [schemaObject?.id]);

  const selectTerritory = useCallback((id) => {
    setActiveTerritoryId(id);
    const territory = territories.find(t => t.id === id) || null;
    setSelectedNode(territory);
    setPanelOpen(false);
    setSchemaObject(territory?.has_image ? territory : null);
    setSchemaDetails(null);
    setActiveFloor(null);
    setBurgerOpen(false);
  }, [territories]);

  // Универсальная функция выбора/перехода к объекту
  const selectObject = useCallback((obj, openPanel = true) => {
    if (!obj) return;
    setSelectedNode(obj);
    if (openPanel) setPanelOpen(true);

    const newSchemaObj = obj.has_image ? obj : null;
    setSchemaObject(prev => {
      // Если schemaObject не меняется — сбросим details вручную чтобы перезагрузить
      if (prev?.id === newSchemaObj?.id && newSchemaObj !== null) {
        setSchemaDetails(null);
        fetchObject(newSchemaObj.id)
          .then(setSchemaDetails)
          .catch(() => setSchemaDetails(null));
        return prev;
      }
      return newSchemaObj;
    });

    if (obj.type === 'floor') setActiveFloor(obj);
    else if (obj.type === 'building') setActiveFloor(null);
  }, []);

  const navigateTo = useCallback((obj) => {
    if (!obj) return;
    let target = obj;
    if (obj.type === 'building') {
      const firstFloor = allObjects
        .filter(o => o.parent_id === obj.id && o.type === 'floor')
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
      if (firstFloor) target = firstFloor;
    }
    selectObject(target, true);
    setTreeOpen(false);
  }, [selectObject, allObjects]);

  const handleSelectNode = useCallback((node) => {
    if (node.type === 'building') {
      setSelectedNode(node);
      setPanelOpen(true);
      setTreeOpen(false);
      const territory = node.parent_id ? allObjects.find(o => o.id === node.parent_id) : null;
      const territorySchema = territory?.has_image ? territory : null;
      setSchemaObject(territorySchema);
      setActiveFloor(null);
      return;
    }
    selectObject(node, true);
    setTreeOpen(false);
  }, [selectObject, allObjects]);

  const handleAreaClick = useCallback((targetObjectId) => {
    if (!targetObjectId) return;
    const target = allObjects.find(o => o.id === targetObjectId);
    if (target) {
      setSelectedNode(target);
      setPanelOpen(true);
      if (target.type === 'building') setActiveFloor(null);
    }
  }, [allObjects]);

  const handleClosePanel = useCallback(() => {
    // Закрываем панель, но оставляем selectedNode (подсветка в дереве сохраняется)
    setPanelOpen(false);
  }, []);

  const handleBack = useCallback(() => {
    if (!selectedNode) return;
    if (selectedNode.type === 'room') {
      const floor = allObjects.find(o => o.id === selectedNode.parent_id);
      if (floor) selectObject(floor, true);
    } else if (selectedNode.type === 'floor') {
      const building = allObjects.find(o => o.id === selectedNode.parent_id);
      if (building) {
        const territory = allObjects.find(o => o.id === building.parent_id);
        if (territory) selectTerritory(territory.id);
      }
    } else if (selectedNode.type === 'building') {
      const territory = allObjects.find(o => o.id === selectedNode.parent_id);
      if (territory) selectTerritory(territory.id);
    }
  }, [selectedNode, allObjects, selectObject, selectTerritory]);

  const schemaBuilding = useMemo(() => {
    const ref = selectedNode;
    if (!ref) return null;
    if (ref.type === 'building') return ref;
    if (ref.type === 'floor') {
      return allObjects.find(o => o.id === ref.parent_id && o.type === 'building') || null;
    }
    return null;
  }, [selectedNode, allObjects]);

  const schemaFloors = useMemo(() => {
    if (!schemaBuilding) return [];
    return allObjects
      .filter(o => o.parent_id === schemaBuilding.id && o.type === 'floor')
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [schemaBuilding, allObjects]);

  const breadcrumbSource = selectedNode || schemaObject;
  const breadcrumb = useMemo(() => {
    if (!breadcrumbSource) return [];
    const path = [];
    let cur = breadcrumbSource;
    while (cur) {
      path.unshift(cur);
      cur = cur.parent_id ? allObjects.find(o => o.id === cur.parent_id) : null;
    }
    return path;
  }, [breadcrumbSource, allObjects]);

  const activeFloorId = activeFloor?.id ?? null;

  if (loading) {
    return (
      <div className={styles.viewer}>
        <div className={styles.loadingState}>
          <FaSpinner className={styles.spinner} size={32} />
          <p>Загрузка данных…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.viewer}>
        <div className={styles.errorState}>Ошибка: {error}</div>
      </div>
    );
  }

  const anyOverlay = burgerOpen || treeOpen;

  return (
    <div className={styles.viewer}>
      <header className={styles.header}>
        <div className={styles.headerLogo}>
          <div className={styles.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="8" width="14" height="8" rx="1.5" fill="white" opacity={0.9} />
              <path d="M1 9L9 3l8 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <div className={styles.logoTitle}>Схема</div>
            <div className={styles.logoSub}>Интерактивная карта</div>
          </div>
        </div>

        <nav className={styles.headerTabs}>
          {territories.map(t => (
            <button
              key={t.id}
              className={`${styles.headerTab} ${activeTerritoryId === t.id ? styles.activeTab : ''}`}
              onClick={() => selectTerritory(t.id)}
            >
              {t.name}
              <span className={styles.tabCount}>
                {allObjects.filter(o => o.parent_id === t.id).length}
              </span>
            </button>
          ))}
        </nav>

        {activeTerritory && (
          <span className={styles.mobileTerritoryName}>{activeTerritory.name}</span>
        )}

        <button
          className={styles.burgerBtn}
          onClick={() => { setBurgerOpen(v => !v); setTreeOpen(false); }}
          aria-label="Выбор территории"
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      {burgerOpen && (
        <div className={styles.territoryDropdown}>
          {territories.map(t => (
            <button
              key={t.id}
              className={`${styles.territoryDropdownItem} ${activeTerritoryId === t.id ? styles.activeDropdownItem : ''}`}
              onClick={() => selectTerritory(t.id)}
            >
              {t.name}
              <span className={styles.tabCount}>
                {allObjects.filter(o => o.parent_id === t.id).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {anyOverlay && (
        <div
          className={styles.overlay}
          onClick={() => { setBurgerOpen(false); setTreeOpen(false); }}
        />
      )}

      <main className={styles.main}>
        <ObjectTree
          allObjects={visibleObjects}
          selectedId={selectedNode?.id}
          onSelect={handleSelectNode}
          expandNodeId={selectedNode?.id}
          isOpen={treeOpen}
        />

        <div className={styles.canvasArea}>
          <div className={styles.mobileToolbar}>
            <button
              className={styles.treeToggleBtn}
              onClick={() => { setTreeOpen(v => !v); setBurgerOpen(false); }}
            >
              ☰ Объекты
            </button>
            <div className={styles.mobileFloors}>
              <FloorSelector
                floors={schemaFloors}
                activeId={activeFloorId}
                onSelect={navigateTo}
                reversed={false}
              />
            </div>
          </div>

          <div className={styles.schemaWrapper}>
            <SchemaCanvas
              objectData={schemaDetails}
              allObjects={allObjects}
              onAreaClick={handleAreaClick}
              highlightObjectId={selectedNode?.id}
            />

            {breadcrumb.length > 0 && (
              <div className={styles.breadcrumb}>
                {breadcrumb.map((item, i) => (
                  <React.Fragment key={item.id}>
                    {i > 0 && <span className={styles.breadcrumbSep}>/</span>}
                    <span className={`${styles.breadcrumbItem} ${i === breadcrumb.length - 1 ? styles.current : ''}`}>
                      {item.name}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}

            {!schemaObject && (
              <div className={styles.hint}>Выберите объект со схемой из дерева слева</div>
            )}

            {selectedNode && selectedNode.type !== 'territory' && (
              <button className={styles.backBtn} onClick={handleBack}>
                ← Назад
              </button>
            )}
          </div>

          <div className={styles.desktopFloors}>
            <FloorSelector
              floors={schemaFloors}
              activeId={activeFloorId}
              onSelect={navigateTo}
            />
          </div>
        </div>

        {selectedNode && panelOpen && (
          <InfoPanel
            key={selectedNode.id}
            object={selectedNode}
            allObjects={allObjects}
            onNavigate={navigateTo}
            onClose={handleClosePanel}
            currentSchemaId={schemaObject?.id}
          />
        )}
      </main>
    </div>
  );
}
